"use client";

import { cn } from "@/lib/utils";
import createGlobe, { type COBEOptions } from "cobe";
import { useTheme } from "next-themes";
import { useEffect, useMemo, useRef } from "react";

interface GlobeSpotsProps {
	className?: string;
}

const createGlobeConfig = (isDark: boolean): COBEOptions => ({
	width: 800,
	height: 800,
	devicePixelRatio: 2,
	phi: 0,
	theta: 0.3,
	dark: isDark ? 1 : 0,
	diffuse: 0.4,
	mapSamples: 16000,
	mapBrightness: 1.2,
	baseColor: isDark ? [0.2, 0.2, 0.2] : [0.8, 0.8, 0.8],
	markerColor: [251 / 255, 100 / 255, 21 / 255],
	glowColor: isDark ? [0.3, 0.3, 0.3] : [0.9, 0.9, 0.9],
	markers: [
		{ location: [14.5995, 120.9842], size: 0.03 },
		{ location: [19.076, 72.8777], size: 0.1 },
		{ location: [23.8103, 90.4125], size: 0.05 },
		{ location: [30.0444, 31.2357], size: 0.07 },
		{ location: [39.9042, 116.4074], size: 0.08 },
		{ location: [-23.5505, -46.6333], size: 0.1 },
		{ location: [19.4326, -99.1332], size: 0.1 },
		{ location: [40.7128, -74.006], size: 0.1 },
		{ location: [34.6937, 135.5022], size: 0.05 },
		{ location: [41.0082, 28.9784], size: 0.06 },
	],
});

export function GlobeSpots({ className = "" }: GlobeSpotsProps) {
	const { theme } = useTheme();
	const phiRef = useRef(0);
	const widthRef = useRef(0);
	const rRef = useRef(0);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const pointerInteracting = useRef(null);
	const pointerInteractionMovement = useRef(0);

	const globeConfig = useMemo(
		() => createGlobeConfig(theme === "dark"),
		[theme],
	);

	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	const updatePointerInteraction = (value: any) => {
		pointerInteracting.current = value;
		if (canvasRef.current) {
			canvasRef.current.style.cursor = value ? "grabbing" : "grab";
		}
	};

	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	const updateMovement = (clientX: any) => {
		if (pointerInteracting.current !== null) {
			const delta = clientX - pointerInteracting.current;
			pointerInteractionMovement.current = delta;
			rRef.current = delta / 200;
		}
	};

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		let globe: ReturnType<typeof createGlobe> | null = null;
		let raf = 0;
		let resizeObserver: ResizeObserver | null = null;

		const measureWidth = () => {
			widthRef.current = canvas.offsetWidth;
		};

		const tick = () => {
			if (!globe) return;
			measureWidth();
			const w = widthRef.current;
			if (w <= 0) {
				raf = requestAnimationFrame(tick);
				return;
			}
			if (!pointerInteracting.current) phiRef.current += 0.005;
			globe.update({
				...globeConfig,
				width: w * 2,
				height: w * 2,
				phi: phiRef.current + rRef.current,
			});
			raf = requestAnimationFrame(tick);
		};

		const stopLoop = () => {
			if (raf) cancelAnimationFrame(raf);
			raf = 0;
		};

		const tryInit = () => {
			if (globe) return;
			measureWidth();
			if (widthRef.current === 0) return;
			globe = createGlobe(canvas, {
				...globeConfig,
				width: widthRef.current * 2,
				height: widthRef.current * 2,
			});
			raf = requestAnimationFrame(tick);
			setTimeout(() => {
				canvas.style.opacity = "1";
			}, 500);
		};

		if (canvas.offsetWidth > 0) {
			tryInit();
		} else {
			resizeObserver = new ResizeObserver(() => {
				if (canvas.offsetWidth > 0) {
					resizeObserver?.disconnect();
					resizeObserver = null;
					tryInit();
				}
			});
			resizeObserver.observe(canvas);
		}

		return () => {
			stopLoop();
			resizeObserver?.disconnect();
			globe?.destroy();
		};
	}, [globeConfig]);

	return (
		<div
			className={cn(
				"absolute inset-0 mx-auto aspect-[1/1] w-full max-w-[600px]",
				className,
			)}
		>
			<canvas
				className={cn(
					"size-full opacity-0 transition-opacity duration-500 [contain:layout_paint_size]",
				)}
				ref={canvasRef}
				onPointerDown={(e) =>
					updatePointerInteraction(
						e.clientX - pointerInteractionMovement.current,
					)
				}
				onPointerUp={() => updatePointerInteraction(null)}
				onPointerOut={() => updatePointerInteraction(null)}
				onMouseMove={(e) => updateMovement(e.clientX)}
				onTouchMove={(e) =>
					e.touches[0] && updateMovement(e.touches[0].clientX)
				}
			/>
		</div>
	);
}
